import { CountdownButton } from '@/components/common/count-down-button';
import { HomeButton } from '@/components/common/home-button';
import { LanguageDropdown, type LanguageDropdownHandle } from '@/components/common/language-dropdown';
import { HelpPopup, ScreenSaverListPopup } from '@/components/feature/home';
import { HomePageSwiper } from '@/components/feature/home/home-page-slider';
import { HomeDetailOverlay } from '@/components/feature/home/home-page-slider/home-detail-overlay';
import { UserIcon } from '@/components/icons';
import { PaperIcon } from '@/components/icons/PaperIcon';
import { DeviceType } from '@/enums';
import { useAccessibilityTTS, useDialogCheck, useHomeMenuAutoHide, useOrientation, useRestoreFocusOnClose } from '@/hooks';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore } from '@/stores/configStore';
import { useHomeStore } from '@/stores/homeStore';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function HomePage() {
    const { t } = useTranslation();
    const { isHorizontal } = useOrientation();
    const { config } = useConfigStore();
    const tts = useConfigStore((state) => state.config?.tts);
    const updateConfig = useConfigStore((state) => state.updateConfig);
    const ui = useConfigStore((state) => state.config?.ui);
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const isScreenSaverFullscreen = useConfigStore((s) => s.config?.ui?.isScreenSaverFullscreen);
    const language = useConfigStore((s) => s.config?.ui?.language);
    const isSignage = config?.server?.machine?.deviceType === DeviceType.SIGNAGE;

    const isHomeMenuVisible = useHomeStore((s) => s.isHomeMenuVisible);
    const isHelpPopupOpen = useHomeStore((s) => s.isHelpPopupOpen);
    const isScreenSaverListOpen = useHomeStore((s) => s.isScreenSaverListOpen);
    const setHelpPopupOpen = useHomeStore((s) => s.setHelpPopupOpen);
    const setScreenSaverListOpen = useHomeStore((s) => s.setScreenSaverListOpen);
    const closeHomePopups = useHomeStore((s) => s.closeHomePopups);
    const isDialogOpen = useDialogCheck();

    const shouldHideCountdownButton = () => {
        return tts?.voice === true && tts?.speaker === 'headphone';
    };

    const shouldShowHomeChrome = !isScreenSaverFullscreen || isHomeMenuVisible;
    const shouldShowHomeControls = shouldShowHomeChrome && !isDialogOpen;
    const shouldShowHomeLanguageDropdown = isScreenSaverFullscreen && isHomeMenuVisible && !isDialogOpen;
    const isScreenSaverHomeMenu = isScreenSaverFullscreen && isHomeMenuVisible;
    const hasHomePopupOpen = isHelpPopupOpen || isScreenSaverListOpen;
    const languageDropdownRef = useRef<LanguageDropdownHandle>(null);
    const pendingHomeDetailFocusRef = useRef(false);
    const prevHomeMenuVisibleRef = useRef(false);

    const isHeadphoneConnected = tts?.voice === true && tts?.speaker === 'headphone';

    useHomeMenuAutoHide({
        enabled: isScreenSaverFullscreen && isHomeMenuVisible && !hasHomePopupOpen && !isHeadphoneConnected && !isDialogOpen,
        onHide: () => {
            languageDropdownRef.current?.close();
        },
    });

    const shouldRunHomeCountdown = isScreenSaverFullscreen ? hasHomePopupOpen : shouldShowHomeChrome || hasHomePopupOpen;

    const { restoreFocus } = useRestoreFocusOnClose({ active: isHelpPopupOpen });
    const { speak } = useAccessibilityTTS();

    const screenSaver = config?.ui?.screenSaver || [];
    const requestStaffHelp = config?.ui?.requestStaffHelp;

    useEffect(() => {
        if (tts?.speaker !== 'headphone') {
            updateConfig({
                ui: {
                    ...ui,
                    zoomIn: false,
                    zoomClosed: true,
                    contrast: false,
                    volume: 3,
                    screenMode: 'fullscreen',
                    zoomLevel: 1,
                },
                tts: {
                    ...tts,
                    voice: false,
                },
            });
        }
    }, []);

    useEffect(() => {
        const contentEl = document.querySelector('[data-nav="content"]') as HTMLElement | null;
        if (!contentEl) return;

        if (isScreenSaverHomeMenu) {
            contentEl.setAttribute('tabindex', '-1');
        } else {
            contentEl.setAttribute('tabindex', '0');
        }

        return () => {
            contentEl.setAttribute('tabindex', '0');
        };
    }, [isScreenSaverHomeMenu]);

    useEffect(() => {
        const justShown = isScreenSaverFullscreen && isHomeMenuVisible && !prevHomeMenuVisibleRef.current;
        prevHomeMenuVisibleRef.current = isHomeMenuVisible && isScreenSaverFullscreen;

        if (justShown) {
            pendingHomeDetailFocusRef.current = true;
        }
        if (!isScreenSaverFullscreen || !isHomeMenuVisible) {
            pendingHomeDetailFocusRef.current = false;
        }
    }, [isHomeMenuVisible, isScreenSaverFullscreen]);

    useEffect(() => {
        if (!pendingHomeDetailFocusRef.current) return;
        if (!isScreenSaverFullscreen || !isHomeMenuVisible || isDialogOpen) return;

        if (!tts?.voice) {
            const cancelTimer = window.setTimeout(() => {
                if (!pendingHomeDetailFocusRef.current) return;
                if (!useConfigStore.getState().config?.tts?.voice) {
                    pendingHomeDetailFocusRef.current = false;
                }
            }, 300);
            return () => window.clearTimeout(cancelTimer);
        }

        pendingHomeDetailFocusRef.current = false;

        const focusHomeDetail = () => {
            const el = document.querySelector('[data-home-detail-focus]') as HTMLElement | null;
            el?.focus();
            return !!el;
        };

        const timer = window.setTimeout(() => {
            if (!useConfigStore.getState().config?.tts?.voice) return;
            if (!focusHomeDetail()) {
                window.setTimeout(focusHomeDetail, 100);
            }
        }, 150);

        return () => window.clearTimeout(timer);
    }, [isHomeMenuVisible, isScreenSaverFullscreen, isDialogOpen, tts?.voice]);

    const handleHelpRequest = () => {
        setHelpPopupOpen(true);
        TrackingService.onHomePopupOpen();
    };

    const handleCloseHelpModal = useCallback(() => {
        restoreFocus();
        setHelpPopupOpen(false);
        TrackingService.onHomePopupClose();
    }, [restoreFocus, setHelpPopupOpen]);

    const handleOpenScreenSaverList = () => {
        speak(t('home.button.screen_saver_list') + ' ' + t('common.button.tts.popup_button'));
        setScreenSaverListOpen(true);
        TrackingService.onHomePopupOpen();
    };

    const handleCloseScreenSaverList = useCallback(() => {
        setScreenSaverListOpen(false);
        TrackingService.onHomePopupClose();
    }, [setScreenSaverListOpen]);

    const mainHeightClass = useMemo(() => {
        if (isSignage || isScreenSaverFullscreen) return 'h-screen';

        if (isHorizontal && screenMode === 'split') return 'h-[calc(879*var(--vh-unit))] mt-[calc(24*var(--vh-unit))]';
        if (isHorizontal) return 'h-[calc(879*var(--vh-unit))]';

        if (!isHorizontal && screenMode === 'split') return 'h-[calc(1756*var(--vh-unit))]';
        return 'h-[calc(1700*var(--vh-unit))]';
    }, [isHorizontal, screenMode, isSignage, isScreenSaverFullscreen]);

    if (isSignage) {
        return (
            <main className={clsx('relative w-full', mainHeightClass)}>
                <HomePageSwiper screenSaver={screenSaver} isSignage />
            </main>
        );
    }

    if (!isScreenSaverFullscreen) {
        return (
            <main className={clsx('relative w-full', mainHeightClass)}>
                <HomePageSwiper screenSaver={screenSaver} />
                <section className="absolute bottom-[calc(20*var(--vh-unit))] left-[calc(24*var(--vw-unit))] flex gap-[calc(12*var(--vh-unit))]">
                    <HomeButton
                        role="button"
                        data-tts-scope="nearby-poi-list-button"
                        data-role={t('common.button.tts.popup_button')}
                        prefix={<PaperIcon color="currentColor" />}
                        data-interactive
                        data-playsound="ting"
                        onClick={() => {
                            speak(t('home.button.screen_saver_list') + ' ' + t('common.button.tts.popup_button'));
                            setScreenSaverListOpen(true);
                            TrackingService.onHomePopupOpen();
                        }}
                        onFocus={() => {
                            speak(
                                t('home.button.screen_saver_list') +
                                    ' ' +
                                    t('common.button.tts.popup_button') +
                                    t('home.title.screen_saver_list_voice')
                            );
                        }}
                    >
                        {t('home.button.screen_saver_list')}
                    </HomeButton>
                    {requestStaffHelp?.status === true && (
                        <HomeButton
                            aria-label={t('employeeHelp.title')}
                            data-role={t('common.button.tts.popup_button')}
                            prefix={<UserIcon />}
                            onClick={handleHelpRequest}
                            data-playsound="ting"
                            data-interactive
                        >
                            {t('employeeHelp.title')}
                        </HomeButton>
                    )}
                </section>

                <HelpPopup isOpen={isHelpPopupOpen} onClose={handleCloseHelpModal} />
                <ScreenSaverListPopup screenSaver={screenSaver} isOpen={isScreenSaverListOpen} onClose={handleCloseScreenSaverList} />
                {(isHelpPopupOpen || isScreenSaverListOpen) && !shouldHideCountdownButton() && (
                    <div style={{ display: 'none' }}>
                        <CountdownButton
                            closeModal={() => {
                                setHelpPopupOpen(false);
                                setScreenSaverListOpen(false);
                            }}
                        />
                    </div>
                )}
            </main>
        );
    }

    return (
        <main className={clsx('relative w-full', mainHeightClass)}>
            <div className="relative z-0 h-full w-full">
                <HomePageSwiper screenSaver={screenSaver} excludeFromFocusOrder={isScreenSaverHomeMenu} />
            </div>
            {shouldShowHomeControls && <HomeDetailOverlay />}
            <section
                className={clsx('absolute bottom-[calc(32*var(--vh-unit))] left-[calc(32*var(--vw-unit))] z-10 flex gap-[calc(12*var(--vh-unit))]', {
                    hidden: !shouldShowHomeControls,
                })}
            >
                <HomeButton
                    role="button"
                    data-tts-scope="nearby-poi-list-button"
                    data-role={t('common.button.tts.popup_button')}
                    prefix={<PaperIcon color="currentColor" />}
                    data-interactive
                    data-playsound="ting"
                    {...(isScreenSaverHomeMenu ? { 'data-region-nav': true, 'data-region-order': 2 } : {})}
                    onClick={handleOpenScreenSaverList}
                    onFocus={() => {
                        speak(
                            t('home.button.screen_saver_list') +
                                ' ' +
                                t('common.button.tts.popup_button') +
                                t('home.title.screen_saver_list_voice')
                        );
                    }}
                >
                    {t('home.button.screen_saver_list')}
                </HomeButton>
                {requestStaffHelp?.status === true && (
                    <HomeButton
                        aria-label={t('employeeHelp.title')}
                        data-role={t('common.button.tts.popup_button')}
                        prefix={<UserIcon />}
                        onClick={handleHelpRequest}
                        data-playsound="ting"
                        data-interactive
                        {...(isScreenSaverHomeMenu ? { 'data-region-nav': true, 'data-region-order': 3 } : {})}
                    >
                        {t('employeeHelp.title')}
                    </HomeButton>
                )}
            </section>

            {shouldShowHomeLanguageDropdown && (
                <section className="absolute right-[calc(24*var(--vw-unit))] bottom-[calc(22*var(--vh-unit))] z-10 flex items-end gap-[calc(12*var(--vh-unit))]">
                    <LanguageDropdown ref={languageDropdownRef} regionNav regionOrder={4} />
                </section>
            )}

            <ScreenSaverListPopup screenSaver={screenSaver} isOpen={isScreenSaverListOpen} onClose={handleCloseScreenSaverList} />
            {shouldRunHomeCountdown && !shouldHideCountdownButton() && (
                <div style={{ display: 'none' }}>
                    <CountdownButton
                        closeModal={() => {
                            closeHomePopups();
                            languageDropdownRef.current?.close();
                        }}
                    />
                </div>
            )}
        </main>
    );
}
